import { Component, OnInit, signal } from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AccountService } from '../../../../Utils/_services/account/account.service';
import { PageTitleService } from '../../../../Utils/_services/page-title/page-title.service';
import { ToastrService } from 'ngx-toastr';
import { ICustomError } from '../../../../Utils/_models/network-response';
import { AdminNavDetailsComponent } from "../../layout/admin-nav-details/admin-nav-details.component";
import { IUser } from './user.model';
import { UserViewComponent } from './user-view/user-view.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import bootstrap from 'bootstrap';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AdminNavDetailsComponent,
    NgIf,
    UserViewComponent
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.css'
})
export class UsersComponent implements OnInit {
  users: any[] = [];
  totalCount: number = 0;
  pageNumber: number = 1;
  pageSize: number = 10;
  totalPages: number = 1;
  isLoading: boolean = false;
  errorMessage: string = '';
  updatingUsers: Set<number> = new Set();


  loading = signal(false);
  isVisible = signal(false);
  isView = signal(false);
  searchTerm = signal<string | null>(null);
  currentPage = signal(1);
  itemsPerPage = signal(10);
  startIndex = signal(1);
  endIndex = signal(10);
  allSelected = signal(false);
  partners = signal<IUser[]>([]);
  partnerType = signal<"Accept" | "Reject">("Accept");

  private modalRef: NgbModalRef | null = null;



  constructor(
    private accountService: AccountService,
    private pageTitleService: PageTitleService,
    private toastr: ToastrService,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void {
    this.pageTitleService.setTitle('Manage Users');
    this.loadUsers();
  }

  loadUsers(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.accountService.GetAllUser(this.pageNumber, this.pageSize).subscribe({
      next: (response) => {
        this.users = response.data.items || [];
        this.totalCount = response.data.totalCount || 0;
        this.totalPages = Math.ceil(this.totalCount / this.pageSize);
        this.isLoading = false;
      },
      error: (err: ICustomError) => {
        console.error('Failed to fetch users', err.error);
        this.errorMessage = 'Failed to load users. Please try again later.';
        this.isLoading = false;
      }
    });
  }

  nextPage(): void {
    if (this.pageNumber < this.totalPages) {
      this.pageNumber++;
      this.loadUsers();
    }
  }

  prevPage(): void {
    if (this.pageNumber > 1) {
      this.pageNumber--;
      this.loadUsers();
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.pageNumber) {
      this.pageNumber = page;
      this.loadUsers();
    }
  }

  onPageSizeChange(): void {
    this.pageNumber = 1;
    this.loadUsers();
  }

  toggleNewsletter(user: any): void {
    if (this.updatingUsers.has(user.id)) return;

    const originalValue = user.newsletter;
    user.newsletter = !user.newsletter;
    this.updatingUsers.add(user.id);
    this.errorMessage = '';

    const updatedUser = {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      userName: user.userName,
      dob: user.dob,
      newsletter: user.newsletter,
      isActive: user.isActive,
      role: user.role
    };

    this.accountService.UpdateUser(updatedUser).subscribe({
      next: () => {
        console.log(`Newsletter updated successfully for user ${user.userName}: ${user.newsletter}`);
        this.toastr.info(
          `Newsletter ${user.newsletter ? 'enabled' : 'disabled'} for ${user.userName}`,
          'Newsletter Updated'
        );
        this.updatingUsers.delete(user.id);
      },
      error: (err: ICustomError) => {
        user.newsletter = originalValue;
        console.error('Failed to update newsletter status:', err.error);
        this.errorMessage = 'Failed to update newsletter status. Please try again.';
        this.toastr.error(
          'Failed to update newsletter status. Please try again.',
          'Update Failed'
        );
        this.updatingUsers.delete(user.id);
      }
    });
  }

  toggleUserStatus(user: any): void {
    if (this.updatingUsers.has(user.id)) return;

    const originalValue = user.isActive;
    user.isActive = !user.isActive;
    this.updatingUsers.add(user.id);
    this.errorMessage = '';

    const updatedUser = {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      userName: user.userName,
      dob: user.dob,
      newsletter: user.newsletter,
      isActive: user.isActive,
      role: user.role
    };

    this.accountService.UpdateUser(updatedUser).subscribe({
      next: () => {
        console.log(`User status updated successfully for ${user.userName}: ${user.isActive ? 'Active' : 'Inactive'}`);
        this.toastr.success(
          `User ${user.userName} is now ${user.isActive ? 'Active' : 'Inactive'}`,
          'User Status Updated'
        );
        this.updatingUsers.delete(user.id);
      },
      error: (err: ICustomError) => {
        user.isActive = originalValue;
        console.error('Failed to update user status:', err.error);
        this.errorMessage = 'Failed to update user status. Please try again.';
        this.toastr.error(
          'Failed to update user status. Please try again.',
          'Update Failed'
        );
        this.updatingUsers.delete(user.id);
      }
    });
  }

  isUserUpdating(userId: number): boolean {
    return this.updatingUsers.has(userId);
  }

  getPaginationArray(): number[] {
    const maxVisible = 5;
    const start = Math.max(1, this.pageNumber - Math.floor(maxVisible / 2));
    const end = Math.min(this.totalPages, start + maxVisible - 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  getStartIndex(): number {
    return (this.pageNumber - 1) * this.pageSize + 1;
  }

  getEndIndex(): number {
    return Math.min(this.pageNumber * this.pageSize, this.totalCount);
  }

  openViewModal() {
    console.log("The view was clicked");
    this.isVisible.set(true);
    this.modalRef = this.modalService.open(UserViewComponent);
    this.modalRef.componentInstance.title = 'Custom Modal Title';
    this.modalRef.componentInstance.data = { id: 1, name: 'Example Item' };
  }

  closeModal(): void {
    if (this.modalRef) {
      this.modalRef.close('Data from modal'); // Pass data back if needed
      this.modalRef = null; // Clear the reference
    }
  }

   // To dismiss the modal (often without a specific result, e.g., on backdrop click)
    dismissMyModal() {
      if (this.modalRef) {
        this.modalRef.dismiss('Reason for dismissal'); // Pass a reason if needed
        this.modalRef = null; // Clear the reference
      }
    }
}
